import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  CheckCircle2, Circle, Upload, FileText, MessageCircle, ClipboardList, Users,
  Trophy, ScrollText, Wrench, Award as AwardIcon, CreditCard, Lock, Send, Loader2, Zap,
  Handshake, ArrowRight, Sparkles
} from 'lucide-react';
import { Link } from 'react-router-dom';
import DocumentUploader from './DocumentUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The 12 lifecycle stages with the backend status that anchors each one
const STAGES = [
  { key: 'internal_approval', label: 'Internal Approval', icon: Lock, status: 'pending_approval' },
  { key: 'publish', label: 'Tender Publication', icon: Send, status: 'approved' },
  { key: 'clarification', label: 'Clarification Period', icon: MessageCircle, status: 'published' },
  { key: 'clarification_closure', label: 'Clarification Closure', icon: MessageCircle, status: 'published' },
  { key: 'quotations', label: 'Quotation Submission', icon: ClipboardList, status: 'published' },
  { key: 'comparative', label: 'Comparative Statement', icon: ClipboardList, status: 'published' },
  { key: 'committee', label: 'Committee Review', icon: Users, status: 'under_committee_review' },
  { key: 'award', label: 'Award Decision', icon: Trophy, status: 'awarded' },
  { key: 'po', label: 'Purchase Order', icon: ScrollText, status: 'po_issued' },
  { key: 'work', label: 'Work Completion', icon: Wrench, status: 'work_completed' },
  { key: 'noc', label: 'NOC', icon: FileText, status: 'noc_issued' },
  { key: 'closure', label: 'Final Payment & Closure', icon: AwardIcon, status: 'closed' },
];

const APPROVAL_DOC_KEYS = [
  { key: 'committee_resolution', label: 'Committee Resolution' },
  { key: 'chairman_approval', label: 'Chairman Approval' },
  { key: 'secretary_approval', label: 'Secretary Approval' },
  { key: 'budget_approval', label: 'Budget Approval' },
  { key: 'sanction_letter', label: 'Sanction Letter' },
];

const TenderLifecycle = ({ tenderId, role, onChanged }) => {
  const { user } = useAuth();
  const isDemoUser = !!(user?.email || '').toLowerCase().includes('demo');
  const [lifecycle, setLifecycle] = useState(null);
  const [bids, setBids] = useState([]);
  const [clarifications, setClarifications] = useState([]);
  const [comparative, setComparative] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const calls = [axios.get(`${API}/tenders/${tenderId}/lifecycle`, { withCredentials: true })];
      if (role === 'buyer' || role === 'admin') {
        calls.push(axios.get(`${API}/bids/tender/${tenderId}`, { withCredentials: true }));
      }
      calls.push(axios.get(`${API}/tenders/${tenderId}/clarifications`, { withCredentials: true }));
      const results = await Promise.allSettled(calls);
      if (results[0].status === 'fulfilled') setLifecycle(results[0].value.data);
      if ((role === 'buyer' || role === 'admin') && results[1]?.status === 'fulfilled') setBids(results[1].value.data);
      const clrIdx = (role === 'buyer' || role === 'admin') ? 2 : 1;
      if (results[clrIdx]?.status === 'fulfilled') setClarifications(results[clrIdx].value.data);
    } catch {
      toast.error('Failed to load lifecycle');
    } finally {
      setLoading(false);
    }
  }, [tenderId, role]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    await load();
    onChanged && onChanged();
  };

  // Use backend-computed stage (advances based on bid/clarification/payment signals)
  const currentStageIdx = lifecycle ? Math.min(11, Math.max(0, (lifecycle.stage ?? 0))) : 0;

  if (loading) {
    return <div className="p-6 text-center text-muted-foreground"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>;
  }
  if (!lifecycle) return null;

  return (
    <div className="space-y-6">
      <StageStepper currentIdx={currentStageIdx} status={lifecycle.status} />

      {isDemoUser && role === 'buyer' && (
        <DemoAdvanceBanner tenderId={tenderId} currentStatus={lifecycle.status} refresh={refresh} />
      )}

      {role === 'buyer' && (lifecycle.status === 'draft' || lifecycle.status === 'pending_approval') && (
        <InternalApprovalPanel tenderId={tenderId} lifecycle={lifecycle} busy={busy} setBusy={setBusy} refresh={refresh} isDemoUser={isDemoUser} />
      )}

      {role === 'buyer' && lifecycle.status === 'approved' && (
        <PublishPanel tenderId={tenderId} busy={busy} setBusy={setBusy} refresh={refresh} />
      )}

      {(lifecycle.status === 'published' || lifecycle.status === 'under_committee_review' || lifecycle.status === 'awarded') && (
        <ClarificationPanel
          tenderId={tenderId}
          role={role}
          clarifications={clarifications}
          status={lifecycle.status}
          refresh={refresh}
        />
      )}

      {role === 'buyer' && (lifecycle.status === 'published' || lifecycle.status === 'under_committee_review') && (
        <ComparativeAndAward
          tenderId={tenderId}
          bids={bids}
          status={lifecycle.status}
          lifecycle={lifecycle}
          comparative={comparative}
          setComparative={setComparative}
          busy={busy} setBusy={setBusy} refresh={refresh}
          isDemoUser={isDemoUser}
        />
      )}

      {role === 'buyer' && lifecycle.status === 'awarded' && (
        <PurchaseOrderPanel tenderId={tenderId} lifecycle={lifecycle} busy={busy} setBusy={setBusy} refresh={refresh} isDemoUser={isDemoUser} />
      )}

      {role === 'buyer' && lifecycle.status === 'po_issued' && (
        <WorkCompletionPanel tenderId={tenderId} busy={busy} setBusy={setBusy} refresh={refresh} isDemoUser={isDemoUser} />
      )}

      {role === 'buyer' && lifecycle.status === 'work_completed' && (
        <NOCPanel tenderId={tenderId} busy={busy} setBusy={setBusy} refresh={refresh} isDemoUser={isDemoUser} />
      )}

      {role === 'buyer' && lifecycle.status === 'noc_issued' && (
        <FinalPaymentAndClosurePanel tenderId={tenderId} lifecycle={lifecycle} busy={busy} setBusy={setBusy} refresh={refresh} isDemoUser={isDemoUser} />
      )}

      {lifecycle.status === 'closed' && (
        <div className="p-5 rounded-lg border-2 border-green-500/40 bg-green-50" data-testid="lifecycle-closed-banner">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-6 w-6 text-green-600" />
            <div>
              <div className="font-bold text-navy">Tender Closed</div>
              <div className="text-xs text-muted-foreground">Closed on {new Date(lifecycle.closed_at || Date.now()).toLocaleDateString()}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// STEPPER
// ─────────────────────────────────────────────
const StageStepper = ({ currentIdx, status }) => (
  <div className="bg-card border border-border rounded-lg p-4 sm:p-5" data-testid="lifecycle-stepper">
    <div className="flex items-center justify-between mb-3">
      <div className="text-sm font-bold text-navy">Procurement Lifecycle</div>
      <div className="text-xs text-muted-foreground">Stage {currentIdx + 1} of {STAGES.length} · <span className="font-semibold uppercase">{status.replace(/_/g, ' ')}</span></div>
    </div>
    <div className="grid grid-cols-3 sm:grid-cols-6 md:grid-cols-12 gap-1.5">
      {STAGES.map((s, i) => {
        const done = i < currentIdx;
        const active = i === currentIdx;
        const Icon = s.icon;
        return (
          <div key={s.key} className="flex flex-col items-center text-center" data-testid={`stage-${i}`}>
            <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 ${active ? 'bg-orange border-orange text-white' : done ? 'bg-green-600 border-green-600 text-white' : 'bg-card border-border text-muted-foreground'}`}>
              {done ? <CheckCircle2 className="h-5 w-5" /> : <Icon className="h-4 w-4" />}
            </div>
            <div className={`text-[10px] mt-1 font-medium leading-tight ${active ? 'text-orange' : done ? 'text-green-700' : 'text-muted-foreground'}`}>
              {s.label}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// ─────────────────────────────────────────────
// 1. INTERNAL APPROVAL
// ─────────────────────────────────────────────
const InternalApprovalPanel = ({ tenderId, lifecycle, busy, setBusy, refresh, isDemoUser }) => {
  const [docs, setDocs] = useState({});
  const existing = lifecycle.internal_approval_docs || {};

  const onUpload = (docKey) => (files) => {
    if (files.length === 0) return;
    const f = files[files.length - 1];
    setDocs(prev => ({ ...prev, [docKey]: { url: f.url, path: f.path, filename: f.filename, size_bytes: f.size_bytes } }));
  };

  const submit = async () => {
    if (Object.keys(docs).length === 0) {
      return toast.error('Upload at least one document to save progress.');
    }
    setBusy(true);
    try {
      await axios.post(`${API}/tenders/${tenderId}/internal-approval`, docs, { withCredentials: true });
      toast.success('Approval documents saved');
      setDocs({});
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setBusy(false); }
  };

  const demoAutoAttach = async () => {
    setBusy(true);
    try {
      const sample = {
        committee_resolution: { url: '#demo', path: 'demo/committee_resolution.pdf', filename: 'Committee_Resolution_MoM.pdf', size_bytes: 234567 },
        chairman_approval:    { url: '#demo', path: 'demo/chairman_approval.pdf',    filename: 'Chairman_Approval_Letter.pdf', size_bytes: 198000 },
        secretary_approval:   { url: '#demo', path: 'demo/secretary_approval.pdf',   filename: 'Secretary_Approval.pdf',       size_bytes: 156000 },
        budget_approval:      { url: '#demo', path: 'demo/budget_approval.pdf',      filename: 'Budget_Sanction.pdf',          size_bytes: 145000 },
        sanction_letter:      { url: '#demo', path: 'demo/sanction_letter.pdf',      filename: 'Final_Sanction_Letter.pdf',    size_bytes: 167000 },
      };
      await axios.post(`${API}/tenders/${tenderId}/internal-approval`, sample, { withCredentials: true });
      toast.success('Sample documents attached — tender approved, ready to publish');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const uploadedCount = APPROVAL_DOC_KEYS.filter(k => existing[k.key] || docs[k.key]).length;

  return (
    <div className="bg-card border border-border rounded-lg p-5" data-testid="lifecycle-internal-approval">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-bold text-navy flex items-center gap-2"><Lock className="h-4 w-4" /> Internal Approval Documents</h3>
        <span className="text-xs font-semibold text-muted-foreground">{uploadedCount} of 5 uploaded</span>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Upload all 5 documents to mark the tender as Approved and ready for publication.</p>

      {isDemoUser && (
        <div className="mb-4 p-3 rounded border-2 border-orange/40 bg-orange/5 flex items-center justify-between gap-3" data-testid="demo-autoattach-approval">
          <div className="text-xs text-foreground/80">
            <span className="font-bold text-navy">Hands-On Demo Shortcut: </span>
            Skip the manual uploads — auto-attach 5 sample approval documents and move to the next stage.
          </div>
          <button
            onClick={demoAutoAttach}
            disabled={busy}
            className="px-3 py-2 text-xs font-bold rounded bg-orange text-white disabled:opacity-40 whitespace-nowrap inline-flex items-center"
            data-testid="demo-autoattach-approval-btn"
          >
            <Zap className="h-3 w-3 mr-1" /> Auto-Attach All 5
          </button>
        </div>
      )}

      <div className="space-y-3">
        {APPROVAL_DOC_KEYS.map(({ key, label }) => {
          const stored = existing[key];
          const staged = docs[key];
          const ready = stored || staged;
          return (
            <div key={key} className={`p-3 rounded border ${ready ? 'border-green-300 bg-green-50/50' : 'border-border bg-background'}`} data-testid={`approval-doc-${key}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-navy flex items-center gap-2">
                  {ready ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <Circle className="h-4 w-4 text-muted-foreground" />}
                  {label}
                </div>
                {stored && <span className="text-[10px] text-green-700 font-mono truncate max-w-[160px]">{stored.filename}</span>}
              </div>
              {!stored && (
                <DocumentUploader
                  files={staged ? [staged] : []}
                  onChange={onUpload(key)}
                  purpose={`approval_${key}`}
                  maxFiles={1}
                />
              )}
            </div>
          );
        })}
      </div>

      <button onClick={submit} disabled={busy || Object.keys(docs).length === 0} className="mt-4 px-5 py-2 rounded bg-navy text-white text-sm font-bold disabled:opacity-40 inline-flex items-center" data-testid="submit-approval-docs-btn">
        <Upload className="h-4 w-4 mr-2" /> {busy ? 'Saving…' : 'Save Approval Documents'}
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────
// 2. PUBLISH
// ─────────────────────────────────────────────
const PublishPanel = ({ tenderId, busy, setBusy, refresh }) => {
  const publish = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/tenders/${tenderId}/publish`, {}, { withCredentials: true });
      toast.success('Tender published! Suppliers are now notified.');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to publish');
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-gold/10 border-2 border-gold/40 rounded-lg p-5" data-testid="lifecycle-publish">
      <h3 className="text-lg font-bold text-navy mb-1">Ready to Publish</h3>
      <p className="text-xs text-muted-foreground mb-3">All 5 internal approval documents are on file. Publishing will notify matching suppliers and open the tender for quotations.</p>
      <button onClick={publish} disabled={busy} className="px-5 py-2 rounded bg-orange text-white text-sm font-bold disabled:opacity-40 inline-flex items-center" data-testid="publish-tender-btn">
        <Send className="h-4 w-4 mr-2" /> {busy ? 'Publishing…' : 'Publish Tender'}
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────
// 3. CLARIFICATIONS
// ─────────────────────────────────────────────
const ClarificationPanel = ({ tenderId, role, clarifications, status, refresh }) => {
  const [question, setQuestion] = useState('');
  const [answerForm, setAnswerForm] = useState({}); // clr_id -> text
  const [busy, setBusy] = useState(false);
  const isPublished = status === 'published';

  const ask = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/tenders/${tenderId}/clarifications`, { question }, { withCredentials: true });
      setQuestion('');
      toast.success('Question submitted');
      await refresh();
    } catch (er) {
      toast.error(er.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const answer = async (clrId) => {
    const text = (answerForm[clrId] || '').trim();
    if (!text) return;
    setBusy(true);
    try {
      await axios.post(`${API}/tenders/${tenderId}/clarifications/${clrId}/answer`, { answer: text }, { withCredentials: true });
      setAnswerForm(prev => ({ ...prev, [clrId]: '' }));
      toast.success('Answer posted');
      await refresh();
    } catch (er) {
      toast.error(er.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-5" data-testid="lifecycle-clarifications">
      <h3 className="text-lg font-bold text-navy mb-1 flex items-center gap-2"><MessageCircle className="h-4 w-4" /> Clarifications</h3>
      <p className="text-xs text-muted-foreground mb-4">
        {role === 'supplier'
          ? 'Other suppliers see anonymized questions. The buyer sees your identity.'
          : 'You see supplier identity. Other suppliers see only anonymized labels.'}
      </p>

      {clarifications.length === 0 && (
        <div className="text-xs text-muted-foreground italic mb-3">No clarifications yet.</div>
      )}

      <div className="space-y-3 mb-4">
        {clarifications.map((c, i) => (
          <div key={c.id} className="p-3 rounded border border-border bg-muted/20" data-testid={`clarification-${i}`}>
            <div className="text-xs text-orange font-semibold mb-1">
              {c.supplier_label || c.supplier_name || 'Supplier'}
            </div>
            <div className="text-sm font-medium text-navy mb-1">Q: {c.question}</div>
            {c.answer ? (
              <div className="text-sm text-foreground/80">A: {c.answer}</div>
            ) : role === 'buyer' ? (
              <div className="mt-2 flex gap-2">
                <input
                  type="text"
                  value={answerForm[c.id] || ''}
                  onChange={(e) => setAnswerForm(prev => ({ ...prev, [c.id]: e.target.value }))}
                  placeholder="Type your answer…"
                  className="flex-1 px-3 py-1.5 text-sm rounded border border-input bg-background"
                  data-testid={`clarification-answer-input-${c.id}`}
                />
                <button onClick={() => answer(c.id)} disabled={busy} className="px-3 py-1.5 text-xs font-bold rounded bg-navy text-white disabled:opacity-40" data-testid={`clarification-answer-btn-${c.id}`}>
                  Reply
                </button>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground italic mt-1">Awaiting buyer response…</div>
            )}
          </div>
        ))}
      </div>

      {role === 'supplier' && isPublished && (
        <form onSubmit={ask} className="flex gap-2" data-testid="clarification-ask-form">
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask the buyer a clarification question…"
            className="flex-1 px-3 py-2 text-sm rounded border border-input bg-background"
            data-testid="clarification-question-input"
          />
          <button type="submit" disabled={busy} className="px-4 py-2 text-sm font-bold rounded bg-orange text-white disabled:opacity-40" data-testid="clarification-submit-btn">
            <Send className="h-3 w-3 inline mr-1" /> Ask
          </button>
        </form>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// 5+6+7. COMPARATIVE + COMMITTEE + AWARD
// ─────────────────────────────────────────────
const ComparativeAndAward = ({ tenderId, bids, status, lifecycle, comparative, setComparative, busy, setBusy, refresh, isDemoUser }) => {
  const [selectedBid, setSelectedBid] = useState('');
  const [committeeNote, setCommitteeNote] = useState('');
  const [evalPath, setEvalPath] = useState(null); // 'direct' | 'negotiation' | null
  const [neg, setNeg] = useState(null);

  const approvalLetter = lifecycle?.committee_approval_letter;
  const inCommitteeReview = status === 'under_committee_review';

  // Fetch any existing negotiation for this tender to expose branch state & post-finalise summary
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/negotiations/by-parent/tender/${tenderId}`, { withCredentials: true });
        if (!cancel && data?.exists) {
          setNeg(data);
          setEvalPath('negotiation');
        }
      } catch { /* silent — no negotiation yet */ }
    })();
    return () => { cancel = true; };
  }, [tenderId]);

  // Auto-load Original Comparative Statement on mount (so both original + revised are visible without an extra click)
  useEffect(() => {
    if (bids.length === 0 || comparative) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/tenders/${tenderId}/comparative-statement`, { withCredentials: true });
        setComparative(data);
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenderId, bids.length]);

  const loadComparative = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/tenders/${tenderId}/comparative-statement`, { withCredentials: true });
      setComparative(data);
    } catch (e) { toast.error('Failed to load comparative statement'); }
    finally { setBusy(false); }
  };

  const submitCommittee = async () => {
    if (!selectedBid) return toast.error('Select the recommended supplier first');
    setBusy(true);
    try {
      const winning = bids.find(b => b.id === selectedBid);
      const winningSupplierId = winning?.supplier?.id || winning?.supplier_id;
      const negRow = (neg?.summary?.final_comparative_rows || []).find(r => r.supplier_id === winningSupplierId);
      const negotiatedAmt = negRow?.effective_amount != null ? Number(negRow.effective_amount) : null;
      const recommendedAmt = negotiatedAmt ?? winning?.quoted_amount;
      const supplierName = winning?.supplier?.company_name || winning?.supplier_name || winningSupplierId;
      // NEW: multi-member Committee Approval Workflow (Phase 1.3). Fallback to legacy single-shot
      // if the Buyer Admin hasn't configured the committee yet.
      try {
        await axios.post(`${API}/tenders/${tenderId}/committee/send`,
          {
            recommended_bid_id: selectedBid,
            recommended_supplier_name: supplierName,
            recommended_amount: recommendedAmt,
            manager_notes: committeeNote,
          },
          { withCredentials: true });
        toast.success('Sent to Committee. Members have been emailed to review & approve.');
      } catch (err) {
        const msg = err.response?.data?.detail || '';
        // If committee not configured, fall back to legacy single-shot for backward compat
        if (/Configure Approval Policy|committee member accounts|Committee Resolution/i.test(msg)) {
          toast.warning('Committee not fully configured. Set up Committee Management first for the multi-member workflow.');
          return;
        }
        throw err;
      }
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const award = async () => {
    if (!selectedBid) return toast.error('Select a bid to award');
    setBusy(true);
    try {
      const winning = bids.find(b => b.id === selectedBid);
      const winningSupplierId = winning?.supplier?.id || winning?.supplier_id;
      // Use negotiated amount if available for this supplier (finalised OR closed round with a revised submission)
      const negRow = (neg?.summary?.final_comparative_rows || []).find(r => r.supplier_id === winningSupplierId);
      const negotiatedAmt = negRow?.effective_amount != null ? Number(negRow.effective_amount) : null;
      const finalAmt = negotiatedAmt ?? winning?.quoted_amount;
      await axios.post(`${API}/tenders/${tenderId}/award`,
        { bid_id: selectedBid, award_amount: finalAmt },
        { withCredentials: true });
      toast.success(negotiatedAmt && negotiatedAmt !== winning?.quoted_amount
        ? `Tender awarded at negotiated amount ₹${Number(negotiatedAmt).toLocaleString('en-IN')}`
        : 'Tender awarded');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  // Post-negotiation helper: pre-select the recommended supplier's bid
  useEffect(() => {
    if (!bids?.length || selectedBid) return;
    const recSupplierId = neg?.final_recommended_supplier_id || neg?.summary?.recommended_supplier_id;
    if (!recSupplierId) return;
    const b = bids.find(x => x.supplier?.id === recSupplierId || x.supplier_id === recSupplierId);
    if (b) setSelectedBid(b.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neg, bids]);

  return (
    <div className="bg-card border border-border rounded-lg p-5" data-testid="lifecycle-comparative-award">
      <h3 className="text-lg font-bold text-navy mb-3 flex items-center gap-2"><ClipboardList className="h-4 w-4" /> Quotations · Comparative Statement · Award</h3>

      {bids.length === 0 ? (
        <div className="text-sm text-muted-foreground italic">No quotations received yet.</div>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap gap-2">
            <button onClick={loadComparative} disabled={busy} className="px-3 py-1.5 text-xs font-bold rounded bg-gold text-navy disabled:opacity-40" data-testid="load-comparative-btn">
              {comparative ? 'Refresh Original Comparative Statement' : 'Generate Original Comparative Statement'}
            </button>
            <a
              href={`${API}/tenders/${tenderId}/comparative-statement/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 text-xs font-bold rounded bg-navy text-white hover:opacity-90 inline-flex items-center"
              data-testid="download-comparative-pdf-btn"
            >
              <FileText className="h-3 w-3 mr-1" /> Download PDF
            </a>
          </div>

          {comparative && comparative.headers && (
            <div className="overflow-x-auto mb-4" data-testid="comparative-statement-table">
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">Original Comparative Statement</div>
              <table className="w-full text-xs sm:text-sm border border-border">
                <thead className="bg-navy text-white">
                  <tr>{comparative.headers.map((h, i) => <th key={i} className="py-2 px-3 text-left">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {comparative.rows.map((r, i) => (
                    <tr key={i} className="border-b border-border odd:bg-muted/20">
                      {r.map((c, j) => <td key={j} className="py-2 px-3">{c}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ─── Revised Comparative Statement (post-negotiation) ─── */}
          {neg?.exists && (neg?.summary?.revised_received_count > 0 || neg?.summary?.current_round_status === 'closed' || neg?.status === 'finalized') && (
            <div className="overflow-x-auto mb-4" data-testid="revised-comparative-statement-table">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-bold uppercase tracking-wider text-orange">Revised (Post-Negotiation) Comparative Statement</div>
                <div className="text-[11px] text-muted-foreground">
                  {neg?.summary?.savings_vs_budget != null
                    ? <>Savings vs Budget: <strong className="text-navy">₹{Number(neg.summary.savings_vs_budget).toLocaleString('en-IN')} ({neg.summary.savings_vs_budget_pct}%)</strong></>
                    : <>Estimated Procurement Budget: <strong className="text-navy">Not Available</strong></>
                  }
                  {neg?.summary?.negotiation_savings_vs_l1 != null && <> · vs L1: <strong className="text-navy">₹{Number(neg.summary.negotiation_savings_vs_l1).toLocaleString('en-IN')} ({neg.summary.negotiation_savings_pct}%)</strong></>}
                </div>
              </div>
              <table className="w-full text-xs sm:text-sm border border-orange/40">
                <thead className="bg-orange text-white">
                  <tr>
                    <th className="py-2 px-3 text-left">Supplier</th>
                    <th className="py-2 px-3 text-right">Original</th>
                    <th className="py-2 px-3 text-right">Revised</th>
                    <th className="py-2 px-3 text-right">Effective</th>
                    <th className="py-2 px-3 text-left">Status</th>
                    <th className="py-2 px-3 text-left"></th>
                  </tr>
                </thead>
                <tbody>
                  {(neg.summary.final_comparative_rows || []).map(row => {
                    const isRec = row.supplier_id === neg.summary.recommended_supplier_id;
                    return (
                      <tr key={row.supplier_id} className={`border-b border-border ${isRec ? 'bg-gold/10' : 'odd:bg-muted/20'}`} data-testid={`revised-cs-row-${row.supplier_id}`}>
                        <td className="py-2 px-3 font-medium">
                          {neg.suppliers?.[row.supplier_id]?.company_name || row.supplier_id}
                          {isRec && <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-gold text-navy">RECOMMENDED</span>}
                        </td>
                        <td className="py-2 px-3 text-right">{row.original_amount != null ? `₹${Number(row.original_amount).toLocaleString('en-IN')}` : '—'}</td>
                        <td className="py-2 px-3 text-right">{row.revised_amount != null ? `₹${Number(row.revised_amount).toLocaleString('en-IN')}` : '—'}</td>
                        <td className="py-2 px-3 text-right font-bold">{row.effective_amount != null ? `₹${Number(row.effective_amount).toLocaleString('en-IN')}` : '—'}</td>
                        <td className="py-2 px-3 text-xs uppercase">
                          {row.response_status === 'submitted' && <span className="text-green-700">Submitted</span>}
                          {row.response_status === 'not_responded' && <span className="text-red-600">Not Responded</span>}
                          {row.response_status === 'declined' && <span className="text-red-600">Declined</span>}
                          {row.response_status === 'pending' && <span className="text-orange">Pending</span>}
                        </td>
                        <td className="py-2 px-3"></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {neg?.status === 'finalized' && (
                <div className="text-[11px] text-muted-foreground mt-1 italic">The recommended supplier and negotiated amount below have been pre-selected from this Revised Comparative Statement.</div>
              )}
            </div>
          )}

          {/* ─── Evaluation Path Chooser (only when no negotiation has been started yet) ─── */}
          {comparative && status === 'published' && !neg?.exists && (
            <div className="mb-5" data-testid="evaluation-path-chooser">
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Choose Evaluation Path</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setEvalPath('direct')}
                  className={`text-left p-4 rounded-lg border-2 transition-colors ${evalPath === 'direct' ? 'border-navy bg-navy/5' : 'border-border hover:border-navy/40'}`}
                  data-testid="path-direct-btn"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Trophy className="h-4 w-4 text-navy" />
                    <div className="font-bold text-navy text-sm">Proceed Without Negotiation</div>
                  </div>
                  <p className="text-xs text-muted-foreground">Select the recommended supplier from the Original Comparative Statement and send the <strong>Manager Recommendation</strong> straight to the Committee.</p>
                </button>
                <Link
                  to={`/buyer/negotiation/tender/${tenderId}`}
                  onClick={() => setEvalPath('negotiation')}
                  className={`block text-left p-4 rounded-lg border-2 transition-colors ${evalPath === 'negotiation' ? 'border-orange bg-orange/5' : 'border-border hover:border-orange/40'}`}
                  data-testid="path-negotiate-btn"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Handshake className="h-4 w-4 text-orange" />
                    <div className="font-bold text-navy text-sm">Start Negotiation</div>
                    <ArrowRight className="h-3 w-3 text-orange ml-auto" />
                  </div>
                  <p className="text-xs text-muted-foreground">Shortlist suppliers, set a deadline, and open the Negotiation Dashboard. Suppliers submit revised quotations; system generates the <strong>Final Comparative Statement</strong> and Manager Recommendation.</p>
                </Link>
              </div>
            </div>
          )}

          {/* Negotiation In-Progress banner — visible when neg is active but not yet finalised */}
          {neg?.exists && neg?.status !== 'finalized' && (
            <div className="mb-4 p-3 rounded border-2 border-orange/30 bg-orange/5 text-xs flex items-center justify-between gap-3 flex-wrap" data-testid="negotiation-in-progress-banner">
              <div>
                <div className="font-bold text-navy">Negotiation in progress · Round {neg.current_round}</div>
                <div className="text-foreground/70">
                  {neg.summary?.current_round_status === 'closed'
                    ? 'Current round is closed. Open the Negotiation dashboard to Finalise & Generate Manager Recommendation.'
                    : `Suppliers can still submit revised quotations. ${neg.summary?.revised_received_count || 0} of ${neg.summary?.shortlisted_count || 0} responded.`}
                </div>
              </div>
              <Link to={`/buyer/negotiation/tender/${tenderId}`} className="px-3 py-1.5 text-xs font-bold rounded bg-orange text-white" data-testid="open-neg-dashboard-btn">Open Negotiation Dashboard →</Link>
            </div>
          )}

          {/* Negotiation-finalised summary — appears when Path B has been used and finalised */}
          {neg?.status === 'finalized' && (
            <div className="mb-5 p-4 rounded-lg border-2 border-orange/40 bg-gold/10" data-testid="negotiation-finalised-card">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-orange" />
                <div className="font-bold text-navy text-sm">Negotiation Finalised — Manager Recommendation Ready</div>
              </div>
              <div className="text-xs text-foreground/80">
                Recommended Supplier: <strong>{neg?.suppliers?.[neg.final_recommended_supplier_id]?.company_name || neg.final_recommended_supplier_id}</strong> · Negotiated Amount: <strong>₹{Number(neg.final_recommended_amount).toLocaleString('en-IN')}</strong>
                {neg.manager_notes && <> · Notes: <em>{neg.manager_notes}</em></>}
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">The Award action below will use the negotiated amount when this supplier is selected.</div>
            </div>
          )}

          {/* Manager Recommendation — bid selection + committee submission
              Shown when: (a) buyer chose direct path, OR
                          (b) negotiation is finalised, OR
                          (c) negotiation round is closed (buyer can send the recommendation without formal finalise) */}
          {(evalPath === 'direct' || neg?.status === 'finalized' || (neg?.exists && neg?.summary?.current_round_status === 'closed')) && (
            <>
              <div className="space-y-2 mb-4" data-testid="manager-recommendation-block">
                <label className="block text-sm font-semibold text-navy">Select recommended supplier</label>
                <select
                  value={selectedBid}
                  onChange={(e) => setSelectedBid(e.target.value)}
                  className="w-full px-3 py-2 rounded border border-input bg-background text-sm"
                  data-testid="select-winning-bid"
                >
                  <option value="">— Select a quotation —</option>
                  {bids.map(b => {
                    const supId = b.supplier?.id || b.supplier_id;
                    const negRow = (neg?.summary?.final_comparative_rows || []).find(r => r.supplier_id === supId);
                    const effective = negRow?.effective_amount != null ? negRow.effective_amount : b.quoted_amount;
                    const isRec = supId === (neg?.final_recommended_supplier_id || neg?.summary?.recommended_supplier_id);
                    const suffix = negRow?.revised_amount != null && negRow.revised_amount !== b.quoted_amount
                      ? ` (revised from ₹${Number(b.quoted_amount).toLocaleString('en-IN')})`
                      : '';
                    return (
                      <option key={b.id} value={b.id}>
                        {isRec ? '★ ' : ''}{b.supplier?.company_name || 'Supplier'} — ₹{Number(effective).toLocaleString('en-IN')}{suffix}
                      </option>
                    );
                  })}
                </select>
              </div>

              {status === 'published' && (
                <div className="mb-3">
                  <input
                    type="text"
                    value={committeeNote}
                    onChange={(e) => setCommitteeNote(e.target.value)}
                    placeholder="Manager Recommendation notes (optional, e.g. rationale / MoM summary)"
                    className="w-full px-3 py-2 text-sm rounded border border-input bg-background mb-2"
                    data-testid="committee-note-input"
                  />
                  <button onClick={submitCommittee} disabled={busy || !selectedBid} className="px-4 py-2 text-xs font-bold rounded bg-navy text-white disabled:opacity-40 mr-2" data-testid="committee-review-btn">
                    <Users className="h-3 w-3 inline mr-1" /> Generate Manager Recommendation (Send to Committee)
                  </button>
                </div>
              )}

              {inCommitteeReview && (
                <div className={`mb-4 p-3 rounded border-2 ${approvalLetter ? 'border-green-300 bg-green-50/50' : 'border-orange/40 bg-orange/5'}`} data-testid="committee-approval-letter-card">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-xs sm:text-sm">
                      <div className="font-bold text-navy inline-flex items-center gap-1">
                        {approvalLetter ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <Circle className="h-4 w-4 text-orange" />}
                        Committee Approval Letter
                      </div>
                      {approvalLetter ? (
                        <div className="text-foreground/80 text-xs mt-0.5">
                          {approvalLetter.filename} · Ref: {approvalLetter.reference || '—'} · Signed by {approvalLetter.issued_by || 'Committee Chairman'}
                        </div>
                      ) : (
                        <div className="text-orange text-xs mt-0.5">Required before awarding the tender.</div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <button onClick={award} disabled={busy || !selectedBid || (inCommitteeReview && !approvalLetter)} className="px-4 py-2 text-sm font-bold rounded bg-orange text-white disabled:opacity-40" data-testid="award-btn" title={inCommitteeReview && !approvalLetter ? 'Upload the Committee Approval Letter first' : ''}>
                <Trophy className="h-4 w-4 inline mr-1" /> Award Selected Supplier
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// 8. PURCHASE ORDER
// ─────────────────────────────────────────────
const PurchaseOrderPanel = ({ tenderId, lifecycle, busy, setBusy, refresh }) => {
  const [form, setForm] = useState({
    po_number: '', contract_start: '', contract_end: '',
    contract_value: lifecycle.awarded_amount || '', payment_terms: '', penalty_clauses: ''
  });
  const [poDoc, setPoDoc] = useState([]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.po_number || !form.contract_start || !form.contract_end || !form.contract_value) {
      return toast.error('Please fill required fields');
    }
    setBusy(true);
    try {
      await axios.post(`${API}/tenders/${tenderId}/purchase-order`, {
        po_number: form.po_number,
        contract_start: new Date(form.contract_start).toISOString(),
        contract_end: new Date(form.contract_end).toISOString(),
        contract_value: parseFloat(form.contract_value),
        payment_terms: form.payment_terms,
        penalty_clauses: form.penalty_clauses,
        po_document: poDoc[0] || null,
      }, { withCredentials: true });
      toast.success('Purchase Order issued');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-card border border-border rounded-lg p-5 space-y-3" data-testid="lifecycle-po">
      <h3 className="text-lg font-bold text-navy flex items-center gap-2"><ScrollText className="h-4 w-4" /> Issue Purchase Order</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <Input label="PO Number *" value={form.po_number} onChange={v => setForm({ ...form, po_number: v })} testid="po-number-input" />
        <Input label="Contract Value (₹) *" type="number" value={form.contract_value} onChange={v => setForm({ ...form, contract_value: v })} testid="po-value-input" />
        <Input label="Contract Start *" type="date" value={form.contract_start} onChange={v => setForm({ ...form, contract_start: v })} testid="po-start-input" />
        <Input label="Contract End *" type="date" value={form.contract_end} onChange={v => setForm({ ...form, contract_end: v })} testid="po-end-input" />
      </div>
      <Input label="Payment Terms" value={form.payment_terms} onChange={v => setForm({ ...form, payment_terms: v })} testid="po-payment-input" />
      <Input label="Penalty Clauses" value={form.penalty_clauses} onChange={v => setForm({ ...form, penalty_clauses: v })} testid="po-penalty-input" />
      <div>
        <label className="block text-xs font-semibold text-navy mb-1">PO Document (optional)</label>
        <DocumentUploader files={poDoc} onChange={setPoDoc} purpose="purchase_order" maxFiles={1} />
      </div>
      <button disabled={busy} className="px-5 py-2 rounded bg-orange text-white text-sm font-bold disabled:opacity-40" data-testid="submit-po-btn">
        {busy ? 'Saving…' : 'Issue Purchase Order'}
      </button>
    </form>
  );
};

// ─────────────────────────────────────────────
// 9. WORK COMPLETION
// ─────────────────────────────────────────────
const WorkCompletionPanel = ({ tenderId, busy, setBusy, refresh }) => {
  const [date, setDate] = useState('');
  const [notes, setNotes] = useState('');
  const [cert, setCert] = useState([]);

  const submit = async (e) => {
    e.preventDefault();
    if (!date) return toast.error('Completion date is required');
    setBusy(true);
    try {
      await axios.post(`${API}/tenders/${tenderId}/work-completion`, {
        completion_date: new Date(date).toISOString(),
        completion_certificate: cert[0] || null,
        notes,
      }, { withCredentials: true });
      toast.success('Work completion recorded');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-card border border-border rounded-lg p-5 space-y-3" data-testid="lifecycle-work-completion">
      <h3 className="text-lg font-bold text-navy flex items-center gap-2"><Wrench className="h-4 w-4" /> Work Completion</h3>
      <Input label="Completion Date *" type="date" value={date} onChange={setDate} testid="wc-date-input" />
      <Input label="Notes" value={notes} onChange={setNotes} testid="wc-notes-input" />
      <div>
        <label className="block text-xs font-semibold text-navy mb-1">Completion Certificate</label>
        <DocumentUploader files={cert} onChange={setCert} purpose="completion_certificate" maxFiles={1} />
      </div>
      <button disabled={busy} className="px-5 py-2 rounded bg-navy text-white text-sm font-bold disabled:opacity-40" data-testid="submit-wc-btn">
        {busy ? 'Saving…' : 'Record Completion'}
      </button>
    </form>
  );
};

// ─────────────────────────────────────────────
// 10. NOC
// ─────────────────────────────────────────────
const NOCPanel = ({ tenderId, busy, setBusy, refresh }) => {
  const [date, setDate] = useState('');
  const [issuedBy, setIssuedBy] = useState('');
  const [doc, setDoc] = useState([]);

  const submit = async (e) => {
    e.preventDefault();
    if (!date) return toast.error('NOC date is required');
    setBusy(true);
    try {
      await axios.post(`${API}/tenders/${tenderId}/noc`, {
        noc_date: new Date(date).toISOString(),
        noc_document: doc[0] || null,
        issued_by: issuedBy,
      }, { withCredentials: true });
      toast.success('NOC recorded');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-card border border-border rounded-lg p-5 space-y-3" data-testid="lifecycle-noc">
      <h3 className="text-lg font-bold text-navy flex items-center gap-2"><FileText className="h-4 w-4" /> NOC (No Objection Certificate)</h3>
      <Input label="NOC Date *" type="date" value={date} onChange={setDate} testid="noc-date-input" />
      <Input label="Issued By" value={issuedBy} onChange={setIssuedBy} testid="noc-issued-by-input" placeholder="e.g. Society Secretary" />
      <div>
        <label className="block text-xs font-semibold text-navy mb-1">NOC Document</label>
        <DocumentUploader files={doc} onChange={setDoc} purpose="noc" maxFiles={1} />
      </div>
      <button disabled={busy} className="px-5 py-2 rounded bg-navy text-white text-sm font-bold disabled:opacity-40" data-testid="submit-noc-btn">
        {busy ? 'Saving…' : 'Upload NOC'}
      </button>
    </form>
  );
};

// ─────────────────────────────────────────────
// 11+12. FINAL PAYMENT + CLOSURE
// ─────────────────────────────────────────────
const FinalPaymentAndClosurePanel = ({ tenderId, lifecycle, busy, setBusy, refresh }) => {
  const [amount, setAmount] = useState(lifecycle.awarded_amount || '');
  const [date, setDate] = useState('');
  const [doc, setDoc] = useState([]);
  const [paymentDone, setPaymentDone] = useState(!!lifecycle.final_payment);

  const submitPayment = async (e) => {
    e.preventDefault();
    if (!amount || !date) return toast.error('Amount and date are required');
    setBusy(true);
    try {
      await axios.post(`${API}/tenders/${tenderId}/final-payment`, {
        paid_amount: parseFloat(amount),
        paid_date: new Date(date).toISOString(),
        settlement_document: doc[0] || null,
      }, { withCredentials: true });
      toast.success('Final payment recorded');
      setPaymentDone(true);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const close = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/tenders/${tenderId}/close`, {}, { withCredentials: true });
      toast.success('Tender closed successfully');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-5 space-y-4" data-testid="lifecycle-final-payment">
      <h3 className="text-lg font-bold text-navy flex items-center gap-2"><CreditCard className="h-4 w-4" /> Final Payment & Closure</h3>

      {!paymentDone ? (
        <form onSubmit={submitPayment} className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Input label="Paid Amount (₹) *" type="number" value={amount} onChange={setAmount} testid="fp-amount-input" />
            <Input label="Payment Date *" type="date" value={date} onChange={setDate} testid="fp-date-input" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-navy mb-1">Settlement Document</label>
            <DocumentUploader files={doc} onChange={setDoc} purpose="final_settlement" maxFiles={1} />
          </div>
          <button disabled={busy} className="px-5 py-2 rounded bg-navy text-white text-sm font-bold disabled:opacity-40" data-testid="submit-final-payment-btn">
            {busy ? 'Saving…' : 'Record Final Payment'}
          </button>
        </form>
      ) : (
        <div className="p-3 rounded bg-green-50 border border-green-200 text-sm text-green-800" data-testid="final-payment-done-banner">
          ✓ Final payment recorded. You can now close the tender.
        </div>
      )}

      <div className="pt-3 border-t border-border">
        <button onClick={close} disabled={busy} className="px-5 py-2 rounded bg-orange text-white text-sm font-bold disabled:opacity-40" data-testid="close-tender-btn">
          <CheckCircle2 className="h-4 w-4 inline mr-1" /> Close Tender
        </button>
        <p className="text-[11px] text-muted-foreground mt-1">Closure is final and locks the tender record.</p>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// SHARED INPUT
// ─────────────────────────────────────────────
const Input = ({ label, value, onChange, type = 'text', testid, placeholder }) => (
  <div>
    <label className="block text-xs font-semibold text-navy mb-1">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 rounded border border-input bg-background text-sm"
      data-testid={testid}
    />
  </div>
);

// ─────────────────────────────────────────────
// DEMO ADVANCE / REWIND BANNER — bidirectional lifecycle for demo users
// ─────────────────────────────────────────────
const DemoAdvanceBanner = ({ tenderId, currentStatus, refresh }) => {
  const [busy, setBusy] = useState(false);

  const act = async (direction) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/tenders/${tenderId}/demo/${direction}`, {}, { withCredentials: true });
      const label = data.label || (direction === 'advance' ? data.next_status : data.prev_status);
      toast.success(`${direction === 'advance' ? '→' : '←'} ${label?.toString().replace(/_/g, ' ')}`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const isInitial = currentStatus === 'draft';
  const isTerminal = currentStatus === 'closed';

  return (
    <div className="p-4 rounded-lg border-2 border-orange/40 bg-gradient-to-r from-orange/10 to-gold/10" data-testid="demo-advance-banner">
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-orange text-white flex items-center justify-center font-bold text-sm">D</div>
        <div className="text-xs sm:text-sm flex-1">
          <div className="font-bold text-navy">Hands-On Demo — Stage Navigation</div>
          <div className="text-foreground/80">Move forward to see each stage populate, or rewind to revisit earlier stages.</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => act('rewind')}
          disabled={busy || isInitial}
          className="px-4 py-2 text-xs font-bold rounded bg-card border-2 border-navy text-navy disabled:opacity-30 inline-flex items-center hover:bg-navy hover:text-white transition-colors"
          data-testid="demo-rewind-btn"
        >
          ← Previous Stage
        </button>
        <button
          onClick={() => act('advance')}
          disabled={busy || isTerminal}
          className="px-4 py-2 text-xs font-bold rounded bg-orange text-white disabled:opacity-40 inline-flex items-center hover:opacity-90"
          data-testid="demo-advance-btn"
        >
          <Zap className="h-3 w-3 mr-1" /> {busy ? 'Working…' : 'Advance Stage →'}
        </button>
      </div>
    </div>
  );
};

export default TenderLifecycle;
